import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BookmarkSimple, FloppyDisk, Plus, Trash, Buildings } from "@phosphor-icons/react";

export const FIXED_SECTIONS = [["location", "Lage"], ["equipment", "Ausstattung"], ["other", "Sonstiges"]];

function LocationTemplates({ propertyId, onInsert, current }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/marketing/text-templates", { params: { kind: "location", property_id: propertyId || undefined } }).then(({ data }) => setList(data || []));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const title = window.prompt("Name der Lage-Vorlage", "Lage");
    if (!title) return;
    try { await api.post("/marketing/text-templates", { kind: "location", title, text: current, property_id: propertyId || null }); toast.success("Als Lage-Vorlage gespeichert"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const del = async (id) => { if (!window.confirm("Diese Lage-Vorlage löschen?")) return; await api.delete(`/marketing/text-templates/${id}`); load(); };
  return (
    <div className="flex gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="mk-location-templates-btn"><BookmarkSimple size={13} /> Vorlage einfügen</Button></PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-1 max-h-80 overflow-y-auto" data-testid="mk-location-templates">
          {!list.length && <div className="p-3 text-xs text-slate-500">Noch keine Lage-Vorlagen. Text schreiben und „Als Vorlage speichern“.</div>}
          {list.map((t) => (
            <div key={t.id} className="group flex items-start gap-2 rounded p-2 hover:bg-slate-50">
              <button type="button" className="flex-1 text-left" onClick={() => { onInsert(t.text); setOpen(false); }} data-testid={`mk-location-template-${t.id}`}>
                <div className="text-sm font-medium flex items-center gap-1">{t.title}{t.for_property && <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700"><Buildings size={10} /> dieses Objekt</span>}</div>
                <div className="text-[11px] text-slate-500 line-clamp-2">{t.text}</div>
              </button>
              <button type="button" onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600" title="Vorlage löschen" data-testid={`mk-location-template-del-${t.id}`}><Trash size={13} /></button>
            </div>
          ))}
        </PopoverContent>
      </Popover>
      <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" disabled={!current?.trim()} onClick={save} data-testid="mk-location-save-template"><FloppyDisk size={13} /> Als Vorlage speichern</Button>
    </div>
  );
}

export function ListingTexts({ listing: l, set }) {
  const secs = l.text_sections || [];
  const get = (k) => secs.find((s) => s.key === k);
  const upsert = (k, patch, title) => {
    const ex = get(k);
    const next = ex ? secs.map((s) => (s.key === k ? { ...s, ...patch } : s)) : [...secs, { key: k, title, text: "", ...patch }];
    const rank = (s) => { const i = FIXED_SECTIONS.findIndex(([fk]) => fk === s.key); return i < 0 ? 99 : i; };
    set("text_sections", next.map((s, i) => [s, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([s]) => s));
  };
  const custom = secs.filter((s) => !FIXED_SECTIONS.some(([k]) => k === s.key));
  const addCustom = () => set("text_sections", [...secs, { key: `c${Date.now().toString(36)}`, title: "", text: "" }]);
  return (
    <div className="space-y-3" data-testid="mk-texts">
      <div><Label className="text-xs">Objektbeschreibung</Label><Textarea rows={6} value={l.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="mk-field-description" /></div>
      {FIXED_SECTIONS.map(([k, title]) => (
        <div key={k}>
          <div className="flex items-end justify-between gap-2 mb-1"><Label className="text-xs">{title}</Label>
            {k === "location" && <LocationTemplates propertyId={l.property_id} current={get(k)?.text || ""}
              onInsert={(txt) => upsert(k, { text: get(k)?.text ? `${get(k).text}\n\n${txt}` : txt }, title)} />}</div>
          <Textarea rows={k === "other" ? 3 : 4} value={get(k)?.text || ""} onChange={(e) => upsert(k, { text: e.target.value }, title)} data-testid={`mk-text-${k}`} />
        </div>
      ))}
      {custom.map((s) => (
        <div key={s.key} className="rounded-md border border-dashed border-slate-300 p-2 space-y-1" data-testid={`mk-text-custom-${s.key}`}>
          <div className="flex gap-2"><Input value={s.title} placeholder="Überschrift (z. B. Energie, Anbindung)" onChange={(e) => upsert(s.key, { title: e.target.value })} className="h-8" data-testid={`mk-text-custom-title-${s.key}`} />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => set("text_sections", secs.filter((x) => x.key !== s.key))} data-testid={`mk-text-custom-del-${s.key}`}><Trash size={14} /></Button></div>
          <Textarea rows={3} value={s.text} onChange={(e) => upsert(s.key, { text: e.target.value })} data-testid={`mk-text-custom-text-${s.key}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="gap-1" onClick={addCustom} data-testid="mk-text-add-section"><Plus size={13} /> Eigenen Abschnitt hinzufügen</Button>
    </div>
  );
}
